import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { X, Pencil, MousePointer2, Trash2, Save, Eraser } from 'lucide-react';

// ---- types ----
export interface FPPoint { x: number; y: number } // normalized 0..1 (y-down)
// A door sits on one wall of a room's bbox: wall 0=top,1=right,2=bottom,3=left;
// pos = centre fraction 0..1 along that wall; width = fraction of the wall length.
export interface FPDoor { id: string; wall: number; pos: number; width: number }
export interface FPRoom { id: string; type: string; label: string; points: FPPoint[]; rect?: boolean; rot?: number; doors?: FPDoor[]; labelScale?: number; trapDir?: string; trapInset?: number; group?: string }
export interface FPLayout { rooms: FPRoom[]; wall?: 'single' | 'double'; labels?: boolean; labelScale?: number }

interface Props {
  open: boolean;
  initialLayout?: FPLayout | null;
  aspect?: number;      // plot width / height (for the editor canvas shape)
  plotName?: string;
  onCancel: () => void;
  onSave: (layout: FPLayout) => void;
}

const ROOM_TYPES: { key: string; label: string; color: string }[] = [
  { key: 'bedroom', label: 'Bedroom', color: '#EAF3FB' },
  { key: 'living', label: 'Living', color: '#EAF7EE' },
  { key: 'kitchen', label: 'Kitchen', color: '#FFF3E0' },
  { key: 'bath', label: 'Bath', color: '#E8F4F8' },
  { key: 'dining', label: 'Dining', color: '#F3EAFB' },
  { key: 'hall', label: 'Hall', color: '#F5F5F5' },
  { key: 'stairs', label: 'Staircase', color: '#ECEFF1' },
  { key: 'other', label: 'Other', color: '#F7F7F7' },
  { key: 'road', label: 'Road', color: '#E6E6E6' },
  { key: 'outline', label: 'Plot Outline', color: '#ffffff' },
];
const COLOR_OF = (t: string) => (ROOM_TYPES.find(r => r.key === t) || ROOM_TYPES[7]).color;
const LABEL_OF = (t: string) => (ROOM_TYPES.find(r => r.key === t) || ROOM_TYPES[7]).label;

const GRID = 0.025;      // snap step in normalized units
const CLOSE_DIST = 0.035; // click-near-first to close polygon
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const snap = (v: number) => Math.round(v / GRID) * GRID;
const dist = (a: FPPoint, b: FPPoint) => Math.hypot(a.x - b.x, a.y - b.y);
const bboxOf = (pts: FPPoint[]) => {
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
};
const rectPts = (b: { x0: number; y0: number; x1: number; y1: number }): FPPoint[] =>
  [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }];
const centroid = (pts: FPPoint[]): FPPoint => {
  let x = 0, y = 0; pts.forEach(p => { x += p.x; y += p.y; }); return { x: x / pts.length, y: y / pts.length };
};
const rotatePt = (p: FPPoint, c: FPPoint, deg: number): FPPoint => {
  const r = deg * Math.PI / 180, cos = Math.cos(r), sin = Math.sin(r);
  const dx = p.x - c.x, dy = p.y - c.y;
  return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
};
let _idc = 0;
const uid = () => `r${Date.now().toString(36)}${(_idc++).toString(36)}`;

export const FloorPlanEditor: React.FC<Props> = ({ open, initialLayout, aspect = 1.35, plotName, onCancel, onSave }) => {
  const VW = 1000;
  const VH = Math.round(1000 / Math.max(0.4, Math.min(2.6, aspect)));
  const svgRef = useRef<SVGSVGElement>(null);

  const [rooms, setRooms] = useState<FPRoom[]>([]);
  const [mode, setMode] = useState<'draw' | 'select'>('draw');
  const [curType, setCurType] = useState('bedroom');
  const [draft, setDraft] = useState<FPPoint[]>([]);
  const [cursor, setCursor] = useState<FPPoint | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  // Floor-plan-wide settings (apply to every room for consistency)
  const [wall, setWall] = useState<'single' | 'double'>('single');
  const [showLabels, setShowLabels] = useState(true);
  const [labelScale, setLabelScale] = useState(1);
  const dragRef = useRef<any>(null);

  // (Re)load when opened
  useEffect(() => {
    if (!open) return;
    setRooms(initialLayout?.rooms ? JSON.parse(JSON.stringify(initialLayout.rooms)) : []);
    setWall(initialLayout?.wall === 'double' ? 'double' : 'single');
    setShowLabels(initialLayout?.labels !== false);
    setLabelScale(initialLayout?.labelScale || 1);
    setDraft([]); setSelId(null); setMode('select'); setCursor(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sel = rooms.find(r => r.id === selId) || null;

  const clientToNorm = useCallback((clientX: number, clientY: number): FPPoint | null => {
    const svg = svgRef.current; if (!svg) return null;
    const pt = svg.createSVGPoint(); pt.x = clientX; pt.y = clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return null;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: clamp01(p.x / VW), y: clamp01(p.y / VH) };
  }, [VW, VH]);

  const nextLabel = useCallback((type: string) => {
    const base = LABEL_OF(type);
    const n = rooms.filter(r => r.type === type).length;
    return n === 0 ? base : `${base} ${n + 1}`;
  }, [rooms]);

  const finalizeRoom = useCallback((points: FPPoint[]) => {
    if (points.length < 3) return;
    // A 4-point free-drawn shape that's (near) axis-aligned is treated as a
    // rectangle so it resizes cleanly; otherwise it's a free polygon.
    const isRect = points.length === 4;
    const room: FPRoom = { id: uid(), type: curType, label: nextLabel(curType), points, rect: isRect };
    setRooms(rs => [...rs, room]);
    // Stay in draw mode so multiple rooms can be drawn back-to-back.
    setDraft([]); setSelId(null); setCursor(null);
  }, [curType, nextLabel]);

  // Drop a ready-made rectangular room/stair of the given type onto the canvas.
  const addReadyRoom = useCallback((type: string) => {
    let b: { x0: number; y0: number; x1: number; y1: number };
    let label: string;
    if (type === 'outline') {
      b = { x0: 0.04, y0: 0.04, x1: 0.96, y1: 0.74 }; label = '';
    } else if (type === 'road') {
      b = { x0: 0.06, y0: 0.82, x1: 0.94, y1: 0.93 }; label = 'ROAD';
    } else {
      const isStair = type === 'stairs';
      const w = isStair ? 0.16 : 0.26, h = isStair ? 0.3 : 0.22;
      const idx = rooms.length;
      const col = idx % 3, row = Math.floor(idx / 3) % 3;
      let x0 = 0.08 + col * 0.3, y0 = 0.08 + row * 0.3;
      x0 = clamp01(Math.min(x0, 1 - w - 0.04)); y0 = clamp01(Math.min(y0, 1 - h - 0.04));
      b = { x0, y0, x1: x0 + w, y1: y0 + h }; label = nextLabel(type);
    }
    const pts = rectPts(b).map(p => ({ x: snap(p.x), y: snap(p.y) }));
    const room: FPRoom = { id: uid(), type, label, points: pts, rect: true };
    setRooms(rs => [...rs, room]);
    setSelId(room.id); setMode('select'); setDraft([]);
  }, [rooms.length, nextLabel]);

  // ---- drawing on the canvas ----
  const onSvgClick = (e: React.MouseEvent) => {
    const p = clientToNorm(e.clientX, e.clientY); if (!p) return;
    if (mode === 'draw') {
      const sp = { x: snap(p.x), y: snap(p.y) };
      if (draft.length >= 3 && dist(sp, draft[0]) < CLOSE_DIST) { finalizeRoom(draft); return; }
      setDraft(d => [...d, sp]);
    } else {
      // background click in select mode → deselect
      if ((e.target as Element).tagName === 'svg' || (e.target as Element).getAttribute('data-bg') === '1') setSelId(null);
    }
  };
  const onSvgMove = (e: React.MouseEvent) => {
    if (mode !== 'draw' || draft.length === 0) { if (cursor) setCursor(null); return; }
    const p = clientToNorm(e.clientX, e.clientY); if (!p) return;
    setCursor({ x: snap(p.x), y: snap(p.y) });
  };
  const onSvgDouble = () => { if (mode === 'draw' && draft.length >= 3) finalizeRoom(draft); };

  // ---- select-mode dragging ----
  const startBodyDrag = (e: React.PointerEvent, id: string) => {
    e.stopPropagation();
    const start = clientToNorm(e.clientX, e.clientY); if (!start) return;
    setSelId(id);
    const room = rooms.find(r => r.id === id); if (!room) return;
    dragRef.current = { kind: 'body', id, start, orig: room.points.map(p => ({ ...p })) };
    attachDrag();
  };
  const startVertexDrag = (e: React.PointerEvent, id: string, idx: number) => {
    e.stopPropagation();
    setSelId(id);
    dragRef.current = { kind: 'vertex', id, idx };
    attachDrag();
  };
  // Rectangle resize: drag a corner/edge handle, keep right angles.
  // Shift+corner = skew that corner (free vertex → irregular shape).
  const startBoxResize = (e: React.PointerEvent, id: string, hk: string) => {
    e.stopPropagation();
    setSelId(id);
    const cornerIdx: Record<string, number> = { tl: 0, tr: 1, br: 2, bl: 3 };
    if (e.shiftKey && cornerIdx[hk] !== undefined) {
      setRooms(rs => rs.map(r => r.id === id ? { ...r, rect: false } : r));
      dragRef.current = { kind: 'vertex', id, idx: cornerIdx[hk] };
    } else {
      dragRef.current = { kind: 'box', id, hk };
    }
    attachDrag();
  };
  const startRotate = (e: React.PointerEvent, id: string) => {
    e.stopPropagation();
    setSelId(id);
    dragRef.current = { kind: 'rotate', id };
    attachDrag();
  };
  const attachDrag = () => {
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragUp);
  };
  const onDragMove = (e: PointerEvent) => {
    const d = dragRef.current; if (!d) return;
    const p = clientToNorm(e.clientX, e.clientY); if (!p) return;
    setRooms(rs => rs.map(r => {
      if (r.id !== d.id) return r;
      if (d.kind === 'rotate') {
        const c = centroid(r.points);
        let deg = Math.atan2(p.y - c.y, p.x - c.x) * 180 / Math.PI + 90;
        deg = Math.round(deg / 5) * 5; // snap to 5°
        return { ...r, rot: ((deg % 360) + 360) % 360 };
      }
      // For rotated rooms, work in the room's un-rotated local space.
      const lp = r.rot ? rotatePt(p, centroid(r.points), -r.rot) : p;
      if (d.kind === 'box') {
        const b = bboxOf(r.points);
        const x = snap(lp.x), y = snap(lp.y), hk = d.hk as string;
        if (hk.indexOf('l') >= 0) b.x0 = Math.min(x, b.x1 - GRID);
        if (hk.indexOf('r') >= 0) b.x1 = Math.max(x, b.x0 + GRID);
        if (hk.indexOf('t') >= 0) b.y0 = Math.min(y, b.y1 - GRID);
        if (hk.indexOf('b') >= 0) b.y1 = Math.max(y, b.y0 + GRID);
        return { ...r, points: rectPts(b) };
      }
      if (d.kind === 'vertex') {
        const pts = r.points.map((pt, i) => i === d.idx ? { x: snap(lp.x), y: snap(lp.y) } : pt);
        return { ...r, points: pts };
      }
      // body move
      const dx = p.x - d.start.x, dy = p.y - d.start.y;
      const pts = d.orig.map((pt: FPPoint) => ({ x: clamp01(snap(pt.x + dx)), y: clamp01(snap(pt.y + dy)) }));
      return { ...r, points: pts };
    }));
  };
  const onDragUp = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragUp);
  };

  // ---- keyboard ----
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (draft.length) setDraft([]); else onCancel(); }
      else if (e.key === 'Enter' && mode === 'draw' && draft.length >= 3) finalizeRoom(draft);
      else if ((e.key === 'Delete' || e.key === 'Backspace') && sel && mode === 'select') {
        setRooms(rs => rs.filter(r => r.id !== sel.id)); setSelId(null);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, draft, mode, sel, finalizeRoom, onCancel]);

  const updateSel = (patch: Partial<FPRoom>) => {
    if (!sel) return;
    setRooms(rs => rs.map(r => r.id === sel.id ? { ...r, ...patch } : r));
  };

  const px = (p: FPPoint) => `${(p.x * VW).toFixed(1)},${(p.y * VH).toFixed(1)}`;
  const pathOf = (pts: FPPoint[], close = true) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${(p.x * VW).toFixed(1)} ${(p.y * VH).toFixed(1)}`).join(' ') + (close ? ' Z' : '');

  const stairLines = (room: FPRoom) => {
    const xs = room.points.map(p => p.x), ys = room.points.map(p => p.y);
    const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
    const w = maxx - minx, h = maxy - miny, horiz = w >= h;
    const longLen = horiz ? w : h;
    const n = Math.max(8, Math.round(longLen / 0.02)); // denser treads
    const out: JSX.Element[] = [];
    for (let k = 1; k < n; k++) {
      const t = k / n;
      if (horiz) { const xx = (minx + w * t) * VW; out.push(<line key={k} x1={xx} y1={miny * VH} x2={xx} y2={maxy * VH} stroke="#8a93a0" strokeWidth={1} />); }
      else { const yy = (miny + h * t) * VH; out.push(<line key={k} x1={minx * VW} y1={yy} x2={maxx * VW} y2={yy} stroke="#8a93a0" strokeWidth={1} />); }
    }
    // centre spine = two flights (up / down)
    if (horiz) { const my = (miny + maxy) / 2 * VH; out.push(<line key="spine" x1={minx * VW} y1={my} x2={maxx * VW} y2={my} stroke="#333" strokeWidth={2} />); }
    else { const mx = (minx + maxx) / 2 * VW; out.push(<line key="spine" x1={mx} y1={miny * VH} x2={mx} y2={maxy * VH} stroke="#333" strokeWidth={2} />); }
    return out;
  };

  const gridLines = useMemo(() => {
    const out: JSX.Element[] = [];
    for (let i = 1; i < Math.round(1 / GRID); i++) {
      const major = i % 4 === 0;
      out.push(<line key={'v' + i} x1={i * GRID * VW} y1={0} x2={i * GRID * VW} y2={VH} stroke="#000" strokeOpacity={major ? 0.09 : 0.045} strokeWidth={major ? 1 : 0.7} />);
    }
    for (let j = 1; j < Math.round(VH / (GRID * VW)); j++) {
      const major = j % 4 === 0;
      out.push(<line key={'h' + j} x1={0} y1={j * GRID * VW} x2={VW} y2={j * GRID * VW} stroke="#000" strokeOpacity={major ? 0.09 : 0.045} strokeWidth={major ? 1 : 0.7} />);
    }
    return out;
  }, [VW, VH]);

  // Selection handles: rectangles get 4 corners + 4 edge midpoints (keep right
  // angles); free polygons get per-vertex handles.
  const rotateHandle = (r: FPRoom) => {
    const b = bboxOf(r.points);
    const cx = (b.x0 + b.x1) / 2, hy = b.y0 - 0.07;
    return (
      <g key="rot">
        <line x1={cx * VW} y1={b.y0 * VH} x2={cx * VW} y2={hy * VH} stroke="#2D9CDB" strokeWidth={1.5} />
        <circle cx={cx * VW} cy={hy * VH} r={8} fill="#2D9CDB" stroke="#fff" strokeWidth={2}
          style={{ cursor: 'grab' }} onPointerDown={(e) => startRotate(e, r.id)}>
          <title>Drag to rotate</title>
        </circle>
      </g>
    );
  };
  const renderHandles = (r: FPRoom) => {
    if (!r.rect) {
      return (
        <>
          {r.points.map((p, i) => (
            <circle key={i} cx={p.x * VW} cy={p.y * VH} r={7} fill="#fff" stroke="#2D9CDB" strokeWidth={2.5}
              style={{ cursor: 'grab' }} onPointerDown={(e) => startVertexDrag(e, r.id, i)} />
          ))}
          {rotateHandle(r)}
        </>
      );
    }
    const b = bboxOf(r.points);
    const cx = (b.x0 + b.x1) / 2, cy = (b.y0 + b.y1) / 2;
    const edges = [
      { k: 't', x: cx, y: b.y0, c: 'ns-resize' }, { k: 'r', x: b.x1, y: cy, c: 'ew-resize' },
      { k: 'b', x: cx, y: b.y1, c: 'ns-resize' }, { k: 'l', x: b.x0, y: cy, c: 'ew-resize' },
    ];
    const corners = [
      { k: 'tl', x: b.x0, y: b.y0, c: 'nwse-resize' }, { k: 'tr', x: b.x1, y: b.y0, c: 'nesw-resize' },
      { k: 'br', x: b.x1, y: b.y1, c: 'nwse-resize' }, { k: 'bl', x: b.x0, y: b.y1, c: 'nesw-resize' },
    ];
    return (
      <>
        {edges.map(h => (
          <rect key={h.k} x={h.x * VW - 5} y={h.y * VH - 5} width={10} height={10} fill="#fff" stroke="#2D9CDB" strokeWidth={2}
            style={{ cursor: h.c }} onPointerDown={(e) => startBoxResize(e, r.id, h.k)} />
        ))}
        {corners.map(h => (
          <circle key={h.k} cx={h.x * VW} cy={h.y * VH} r={7} fill="#fff" stroke="#2D9CDB" strokeWidth={2.5}
            style={{ cursor: h.c }} onPointerDown={(e) => startBoxResize(e, r.id, h.k)} />
        ))}
        {rotateHandle(r)}
      </>
    );
  };

  if (!open) return null;

  const btn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 7, width: '100%',
    background: active ? '#FFD93D' : '#1f1f1f', color: active ? '#1a1a1a' : '#ddd',
    border: '1px solid ' + (active ? '#FFD93D' : '#333'), borderRadius: 8,
    padding: '9px 11px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  });
  const seg = (active: boolean): React.CSSProperties => ({
    background: active ? '#FFD93D' : '#1a1a1a', color: active ? '#1a1a1a' : '#bbb',
    border: '1px solid ' + (active ? '#FFD93D' : '#333'), borderRadius: 6,
    padding: '5px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  });
  const szBtn: React.CSSProperties = {
    background: '#1a1a1a', color: '#ddd', border: '1px solid #333', borderRadius: 6,
    width: 28, height: 26, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.65)', display: 'flex' }}>
      <div style={{ margin: 'auto', width: '96vw', height: '94vh', background: '#161616', borderRadius: 14, display: 'flex', overflow: 'hidden', border: '1px solid #2a2a2a' }}>
        {/* Side panel */}
        <aside style={{ width: 260, flexShrink: 0, background: '#0d0d0d', borderRight: '1px solid #222', display: 'flex', flexDirection: 'column', padding: 16, gap: 12, overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ color: '#FFD93D', fontSize: 16, fontWeight: 800 }}>Floor Plan</div>
              {plotName && <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>{plotName}</div>}
            </div>
            <button onClick={onCancel} title="Close" style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}><X size={18} /></button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn(mode === 'draw'), justifyContent: 'center' }} onClick={() => { setMode('draw'); setSelId(null); }}><Pencil size={15} /> Draw</button>
            <button style={{ ...btn(mode === 'select'), justifyContent: 'center' }} onClick={() => { setMode('select'); setDraft([]); }}><MousePointer2 size={15} /> Select</button>
          </div>

          <div>
            <div style={{ color: '#777', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
              {mode === 'draw' ? 'Draw as…' : 'Add ready room (click)'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {ROOM_TYPES.map(rt => {
                const active = mode === 'draw' && curType === rt.key;
                return (
                  <button key={rt.key}
                    onClick={() => { if (mode === 'draw') setCurType(rt.key); else addReadyRoom(rt.key); }}
                    title={mode === 'draw' ? `Draw a ${rt.label}` : `Add a ready ${rt.label}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: active ? '#23303a' : '#1a1a1a', border: '1px solid ' + (active ? '#2D9CDB' : '#2a2a2a'), color: '#ddd', borderRadius: 7, padding: '7px 8px', fontSize: 12, cursor: 'pointer' }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: rt.color, border: '1px solid #999', flexShrink: 0 }} />
                    {rt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ borderTop: '1px solid #222', paddingTop: 12 }}>
            <div style={{ color: '#777', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Plan settings</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#aaa', fontSize: 12 }}>Walls</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={seg(wall === 'single')} onClick={() => setWall('single')}>Single</button>
                <button style={seg(wall === 'double')} onClick={() => setWall('double')}>Double</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#aaa', fontSize: 12 }}>Labels</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button style={seg(showLabels)} onClick={() => setShowLabels(true)}>Show</button>
                <button style={seg(!showLabels)} onClick={() => setShowLabels(false)}>Hide</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: showLabels ? 1 : 0.4 }}>
              <span style={{ color: '#aaa', fontSize: 12 }}>Label size</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button style={szBtn} disabled={!showLabels} onClick={() => setLabelScale(s => Math.max(0.5, +(s - 0.15).toFixed(2)))}>A−</button>
                <span style={{ color: '#ddd', fontSize: 12, minWidth: 38, textAlign: 'center' }}>{Math.round(labelScale * 100)}%</span>
                <button style={szBtn} disabled={!showLabels} onClick={() => setLabelScale(s => Math.min(2.5, +(s + 0.15).toFixed(2)))}>A+</button>
              </div>
            </div>
          </div>

          {sel && (
            <div style={{ background: '#141414', border: '1px solid #222', borderRadius: 8, padding: 10 }}>
              <div style={{ color: '#777', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Selected room</div>
              <label style={{ color: '#888', fontSize: 11, display: 'block', marginBottom: 3 }}>Label</label>
              <input value={sel.label} onChange={e => updateSel({ label: e.target.value })} placeholder="e.g. Master Bedroom"
                style={{ width: '100%', boxSizing: 'border-box', background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 6, padding: '7px 8px', fontSize: 13, marginBottom: 8 }} />
              <label style={{ color: '#888', fontSize: 11, display: 'block', marginBottom: 3 }}>Type</label>
              <select value={sel.type}
                onChange={e => updateSel({ type: e.target.value, label: sel.label === LABEL_OF(sel.type) ? LABEL_OF(e.target.value) : sel.label })}
                style={{ width: '100%', boxSizing: 'border-box', background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 6, padding: '7px 8px', fontSize: 13, marginBottom: 8 }}>
                {ROOM_TYPES.map(rt => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
              </select>
              <button onClick={() => { setRooms(rs => rs.filter(r => r.id !== sel.id)); setSelId(null); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', background: '#2a1414', border: '1px solid #5a2a2a', color: '#ef6a6a', borderRadius: 7, padding: '8px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Trash2 size={14} /> Delete room
              </button>
            </div>
          )}

          <div style={{ color: '#666', fontSize: 11, lineHeight: 1.5, background: '#141414', borderRadius: 8, padding: 10 }}>
            {mode === 'draw'
              ? <>Click to drop wall points. Click the <b style={{ color: '#FFD93D' }}>first point</b> again, double-click, or press <b>Enter</b> to close the room. <b>Esc</b> cancels.</>
              : <>Click a type above to <b style={{ color: '#FFD93D' }}>add a ready room</b>. Drag the body to move, the <b style={{ color: '#2D9CDB' }}>dots</b> to resize, the top <b style={{ color: '#2D9CDB' }}>○</b> to rotate. <b>Shift</b>-drag a corner to skew it. <b>Delete</b> removes it.</>}
          </div>

          <div style={{ flex: 1 }} />
          <div style={{ color: '#555', fontSize: 11 }}>{rooms.length} room{rooms.length === 1 ? '' : 's'}</div>
          <button onClick={() => { setRooms([]); setDraft([]); setSelId(null); }} style={{ ...btn(false), justifyContent: 'center', color: '#999' }}><Eraser size={14} /> Clear all</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} style={{ ...btn(false), justifyContent: 'center' }}>Cancel</button>
            <button onClick={() => onSave({ rooms, wall, labels: showLabels, labelScale })} style={{ ...btn(true), justifyContent: 'center', background: '#FFD93D' }}><Save size={15} /> Save</button>
          </div>
        </aside>

        {/* Canvas */}
        <main style={{ flex: 1, background: '#202020', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'hidden' }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VW} ${VH}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ background: '#fff', borderRadius: 4, boxShadow: '0 8px 40px rgba(0,0,0,.5)', maxWidth: '100%', maxHeight: '100%', width: VW, height: VH, cursor: mode === 'draw' ? 'crosshair' : 'default', touchAction: 'none' }}
            onClick={onSvgClick}
            onMouseMove={onSvgMove}
            onDoubleClick={onSvgDouble}
          >
            <rect data-bg="1" x={0} y={0} width={VW} height={VH} fill="#fff" />
            {gridLines}
            <rect data-bg="1" x={1} y={1} width={VW - 2} height={VH - 2} fill="none" stroke="#c00" strokeOpacity={0.5} strokeWidth={2} strokeDasharray="6 5" />

            {/* rooms */}
            {[...rooms].sort((a, c) => {
              const o = (t: string) => t === 'outline' ? 0 : t === 'road' ? 1 : 2;
              return o(a.type) - o(c.type);
            }).map(r => {
              const selected = r.id === selId;
              const cc = centroid(r.points);
              const rotAttr = r.rot ? `rotate(${r.rot} ${(cc.x * VW).toFixed(1)} ${(cc.y * VH).toFixed(1)})` : undefined;
              return (
                <g key={r.id} transform={rotAttr}>
                  {wall === 'double' ? (
                    <>
                      <path d={pathOf(r.points)} fill={COLOR_OF(r.type)} fillOpacity={0.95}
                        stroke={selected ? '#2D9CDB' : '#222'} strokeWidth={7} strokeLinejoin="round"
                        style={{ cursor: mode === 'select' ? 'move' : 'inherit' }}
                        onPointerDown={mode === 'select' ? (e) => startBodyDrag(e, r.id) : undefined} />
                      <path d={pathOf(r.points)} fill="none" stroke="#fff" strokeWidth={4} strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
                    </>
                  ) : (
                    <path d={pathOf(r.points)} fill={COLOR_OF(r.type)} fillOpacity={0.95}
                      stroke={selected ? '#2D9CDB' : '#222'} strokeWidth={selected ? 2.6 : 1.8} strokeLinejoin="round"
                      style={{ cursor: mode === 'select' ? 'move' : 'inherit' }}
                      onPointerDown={mode === 'select' ? (e) => startBodyDrag(e, r.id) : undefined} />
                  )}
                  {r.type === 'stairs' && stairLines(r)}
                  {showLabels && r.type !== 'stairs' && (() => {
                    const c = r.points.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
                    const cx = (c.x / r.points.length) * VW, cy = (c.y / r.points.length) * VH;
                    const lf = VH * 0.03 * labelScale; // proportional, matches engine/PDF ratio
                    return <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={lf} fontWeight={700} fill="#333" style={{ pointerEvents: 'none', userSelect: 'none' }}>{r.label.toUpperCase()}</text>;
                  })()}
                  {selected && mode === 'select' && renderHandles(r)}
                </g>
              );
            })}

            {/* draft polygon being drawn */}
            {draft.length > 0 && (
              <g style={{ pointerEvents: 'none' }}>
                <path d={pathOf(draft, false)} fill="none" stroke="#2D9CDB" strokeWidth={2.2} strokeDasharray="2 0" />
                {cursor && <line x1={draft[draft.length - 1].x * VW} y1={draft[draft.length - 1].y * VH} x2={cursor.x * VW} y2={cursor.y * VH} stroke="#2D9CDB" strokeWidth={1.6} strokeDasharray="5 4" />}
                {draft.map((p, i) => (
                  <circle key={i} cx={p.x * VW} cy={p.y * VH} r={i === 0 ? 8 : 5}
                    fill={i === 0 ? '#FFD93D' : '#2D9CDB'} stroke="#fff" strokeWidth={2} />
                ))}
              </g>
            )}
          </svg>
        </main>
      </div>
    </div>
  );
};

export default FloorPlanEditor;
